import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import images from "../../assets/images";
import OwlCarousel from "../../components/carousel";
import TemplateLayout from "../../components/layouts/templatelayout";
import Loader from "../../components/loader";
import ShareModal from "../../components/modal/ShareModal";
import StickyActionBar from "../../components/StickyActionBar";
import { useTranslate } from "../../context/TranslationContext";
import { useGetCategoriesQuery } from "../../redux/Apis/Category";
import { useGetContentQuery } from "../../redux/Apis/Content";
import { useGetAllTemplatesQuery } from "../../redux/Apis/Template";
import "./style.css";

const getAudienceParts = (audience) => {
  if (!audience) return [];

  const paragraphs = audience.match(/<p\b[^>]*>[\s\S]*?<\/p>/gi);
  const entries = paragraphs || [audience];

  return entries
    .flatMap((entry) => {
      const text = entry.replace(/<\/?[^>]+(>|$)/g, "").trim();
      if (!text) return [];

      const tokens = text.split(/\s+/).filter(Boolean);
      if (tokens.length <= 5) return [text];

      const groups = [];
      for (let i = 0; i < tokens.length; i += 4) {
        const chunk = tokens.slice(i, i + 4).join(" ");
        if (chunk) groups.push(chunk);
      }

      return groups;
    })
    .filter(Boolean);
};

const AudienceTags = ({ audience }) => {
  const [visibleCount, setVisibleCount] = useState(0);
  const measureRef = useRef(null);

  const audienceParts = useMemo(() => getAudienceParts(audience), [audience]);

  useEffect(() => {
    const measureVisibleCount = () => {
      if (!measureRef.current || !audienceParts.length) {
        setVisibleCount(0);
        return;
      }

      const tags = Array.from(
        measureRef.current.querySelectorAll("[data-measure-tag]"),
      );
      if (!tags.length) {
        setVisibleCount(0);
        return;
      }

      const firstTop = tags[0].offsetTop;
      let count = 0;
      let currentRow = -1;

      tags.forEach((tag) => {
        if (count === 0 || tag.offsetTop > currentRow) {
          currentRow = tag.offsetTop;
        }

        if (tag.offsetTop - firstTop > 1 && currentRow !== tag.offsetTop) {
          return;
        }

        if (tag.offsetTop > firstTop + 1 && count >= 0) {
          const nextRowTop = tag.offsetTop;
          if (nextRowTop > firstTop + 1) {
            const hasThirdRow = tags.some(
              (item) => item.offsetTop > nextRowTop + 1,
            );
            if (hasThirdRow) {
              return;
            }
          }
        }

        count += 1;
      });

      setVisibleCount(Math.max(1, count));
    };

    measureVisibleCount();
    if (typeof ResizeObserver === "undefined") return undefined;

    const resizeObserver = new ResizeObserver(() => measureVisibleCount());
    resizeObserver.observe(measureRef.current);
    return () => resizeObserver.disconnect();
  }, [audienceParts]);

  if (!audienceParts.length) return null;

  const displayedParts = audienceParts.slice(0, visibleCount);
  const remainingCount = Math.max(
    audienceParts.length - displayedParts.length,
    0,
  );

  return (
    <>
      <div
        ref={measureRef}
        className="template-audience-list template-audience-list--measure"
        aria-hidden="true"
      >
        {audienceParts.map((part, index) => (
          <div
            key={`${part}-${index}`}
            className="template-audience"
            data-measure-tag
          >
            {part.trim()}
          </div>
        ))}
      </div>

      <div className="template-audience-list">
        {displayedParts.map((part, index) => (
          <div key={`${part}-${index}`} className="template-audience">
            {part.trim()}
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="template-audience" data-audience-overflow>
            +{remainingCount}
          </div>
        )}
      </div>
    </>
  );
};

const Template = () => {
  const navigate = useNavigate();
  const { t } = useTranslate("templateList");
  const lang = useSelector((state) => state.language.lang);
  const { user } = useSelector((state) => state.authSlice);
  const [showShareModal, setShowShareModal] = useState(false);
  const userImage = useMemo(() => {
    if (!user?.picture) return images.profile.placeholder;
    if (user.picture.startsWith("http")) return user.picture;
    return user.picture.startsWith("images/")
      ? `${process.env.REACT_APP_S3_BASE_URL}${user.picture}`
      : images.profile.placeholder;
  }, [user?.picture]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { data: featuredVideos, isLoading: isFeaturedVideosLoading } =
    useGetContentQuery({
      search: "featured-videos",
      lang,
    });

  const templateVideoUrl =
    featuredVideos?.data?.sections?.find(
      (s) => s.title === "Featured Template Video",
    )?.content || "";

  const getEmbedUrl = (url, autoplay = true) => {
    if (!url) return null;

    try {
      const urlObj = new URL(url);

      if (
        !urlObj.hostname.includes("youtube.com") &&
        !urlObj.hostname.includes("youtu.be")
      ) {
        return null;
      }

      const videoId = urlObj.searchParams.get("v");
      const listId = urlObj.searchParams.get("list");

      if (listId) {
        return `https://www.youtube.com/embed/videoseries?list=${listId}${
          autoplay ? "&autoplay=1&mute=1" : ""
        }`;
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}${
          autoplay ? "?autoplay=1&mute=1" : ""
        }`;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const embedUrl = getEmbedUrl(templateVideoUrl, !isMobile);

  const { data: categoryData, isLoading: isCategoryLoading } =
    useGetCategoriesQuery({ lang });

  const categories = categoryData?.data || [];

  const { data: templateData, isLoading: isTemplateLoading } =
    useGetAllTemplatesQuery(
      { status: "approved", lang },
      { refetchOnMountOrArgChange: true },
    );

  const templates = useMemo(() => templateData?.data || [], [templateData]);

  const groupedTemplates = useMemo(() => {
    const groups = {};
    templates.forEach((tpl) => {
      const catId = tpl.category?._id;
      if (!groups[catId]) groups[catId] = [];
      groups[catId].push(tpl);
    });
    return groups;
  }, [templates]);

  if (isTemplateLoading || isCategoryLoading || isFeaturedVideosLoading) {
    return (
      <>
        <TemplateLayout />
        <Loader loading={true} />
      </>
    );
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>
          Fitness Templates | Ready-to-Use Training Resources | Traineego
        </title>

        <meta
          name="description"
          content="Access professionally designed fitness templates, workout plans, coaching resources, and productivity tools."
        />

        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://traineego.com/template" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Fitness Template",
            url: "https://traineego.com/template",
            description:
              "Access professionally designed fitness templates, workout plans, coaching resources, and productivity tools.",
            publisher: {
              "@type": "Organization",
              name: "Traineego",
              url: "https://traineego.com",
            },
          })}
        </script>
      </Helmet>
      <TemplateLayout>
        <section
          style={{
            backgroundColor: "#000",
            color: "#fff",
            padding: "40px 0",
            minHeight: "100vh",
          }}
        >
          {embedUrl && (
            <div className="container mb-5">
              <div className="video-container ratio ratio-16x9 mb-5">
                <iframe
                  src={embedUrl}
                  title="Featured Template Video"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  style={{ borderRadius: "12px" }}
                />
              </div>
            </div>
          )}

          <div className="sticky-header-wrapper mb-5">
            <div className="container my-2">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                <p
                  className="text-white fw-bold mb-0"
                  style={{ fontSize: "28px", flexGrow: 1 }}
                >
                  {t("title")}
                </p>

                <div style={{ width: "100%", maxWidth: "280px" }}>
                  <Select
                    isClearable
                    placeholder={t("select")}
                    value={
                      selectedCategory
                        ? {
                            value: selectedCategory,
                            label:
                              categories.find((c) => c._id === selectedCategory)
                                ?.name || "Unknown",
                          }
                        : null
                    }
                    onChange={(option) =>
                      setSelectedCategory(option ? option.value : "")
                    }
                    options={categories.map((c) => ({
                      value: c._id,
                      label: c.name,
                    }))}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderRadius: "6px",
                        minHeight: "38px",
                        fontSize: "14px",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected
                          ? "var(--primaryColor)"
                          : isFocused
                            ? "#e9ecef"
                            : "#f8f9fa",
                        color: isSelected ? "#ffffff" : "#000000",
                        cursor: "pointer",
                        ":active": {
                          ...base[":active"],
                          backgroundColor: "#0056b3",
                        },
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="container">
            {selectedCategory
              ? (() => {
                  const selectedCat = categories.find(
                    (c) => c._id === selectedCategory,
                  );
                  const catTemplates = groupedTemplates[selectedCategory] || [];

                  if (!catTemplates.length) {
                    return (
                      <div className="text-center text-muted py-5">
                        <h5>
                          {t("notFound")} {selectedCat?.name || t("thisCat")}.
                        </h5>
                      </div>
                    );
                  }

                  return (
                    <section className="mb-5" key={selectedCategory}>
                      <div className="row mb-1">
                        <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                          <h4 className="text-uppercase mb-0 me-2">
                            {selectedCat?.name}
                          </h4>
                          <p className="mb-0 m-purple-text">
                            {catTemplates.length}{" "}
                            {catTemplates.length > 1
                              ? t("templates")
                              : t("template")}
                          </p>
                        </div>
                      </div>

                      <OwlCarousel
                        className="category-carousel"
                        options={{
                          margin: 10,
                          loop: false,
                          nav: true,
                          dots: false,
                          lazyLoad: true,
                          autoplay: true,
                          autoplayTimeout: 5000,
                          autoplayHoverPause: true,
                          smartSpeed: 500,
                          navText: [
                            '<i class="fas fa-angle-left"></i>',
                            '<i class="fas fa-angle-right"></i>',
                          ],
                          responsive: {
                            0: { items: 2 },
                            576: { items: 2 },
                            768: { items: 3 },
                            992: { items: 4 },
                            1200: { items: 5 },
                          },
                        }}
                      >
                        {catTemplates.map((tpl) => (
                          <div key={tpl._id} className="template-card-wrapper">
                            <div className="template-card">
                              <img
                                src={
                                  tpl?.file?.startsWith("http")
                                    ? tpl.file
                                    : images.dummy.course
                                }
                                alt={tpl.title_hook || tpl.title}
                                className="template-card-img"
                              />
                              <div className="p-3 d-flex flex-column flex-grow-1">
                                <h6 className="template-card-title fw-bold">
                                  {tpl.title_hook || tpl.title}
                                </h6>
                                <p
                                  className="template-card-subtitle"
                                  dangerouslySetInnerHTML={{
                                    __html: tpl.subtitle || "",
                                  }}
                                />
                                <AudienceTags audience={tpl.audience} />

                                <p className="template-card-access">
                                  {tpl.access_type === "paid"
                                    ? t("full")
                                    : tpl.access_type === "free"
                                      ? t("free")
                                      : t("Membership")}
                                </p>
                                <div className="mt-auto">
                                  <button
                                    className="site-btn mt-2"
                                    style={{ minWidth: "fit-content" }}
                                    onClick={() =>
                                      navigate(`/templates/${tpl._id}`)
                                    }
                                  >
                                    {t("view")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </OwlCarousel>
                    </section>
                  );
                })()
              : categories.map((cat) => {
                  const catTemplates = groupedTemplates[cat._id] || [];
                  if (!catTemplates.length) return null;

                  return (
                    <section className="mb-5" key={cat._id}>
                      <div className="row mb-1">
                        <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                          <h4 className="text-uppercase mb-0 me-2">
                            {cat.name}
                          </h4>
                          <p className="mb-0 m-purple-text">
                            {catTemplates.length}{" "}
                            {catTemplates.length > 1
                              ? t("templates")
                              : t("template")}
                          </p>
                        </div>
                      </div>

                      <OwlCarousel
                        className="category-carousel"
                        options={{
                          margin: 10,
                          loop: false,
                          nav: true,
                          dots: false,
                          lazyLoad: true,
                          autoplay: true,
                          autoplayTimeout: 5000,
                          autoplayHoverPause: true,
                          smartSpeed: 500,
                          navText: [
                            '<i class="fas fa-angle-left"></i>',
                            '<i class="fas fa-angle-right"></i>',
                          ],
                          responsive: {
                            0: { items: 2 },
                            576: { items: 2 },
                            768: { items: 3 },
                            992: { items: 4 },
                            1200: { items: 5 },
                          },
                        }}
                      >
                        {catTemplates.map((tpl) => (
                          <div key={tpl._id} className="template-card-wrapper">
                            <div className="template-card">
                              <img
                                src={
                                  tpl?.file?.startsWith("http")
                                    ? tpl.file
                                    : images.dummy.course
                                }
                                alt={tpl.title_hook || tpl.title}
                                className="template-card-img"
                              />
                              <div className="p-3 d-flex flex-column flex-grow-1">
                                <h6 className="template-card-title fw-bold">
                                  {tpl.title_hook || tpl.title}
                                </h6>
                                <p
                                  className="template-card-subtitle"
                                  dangerouslySetInnerHTML={{
                                    __html: tpl.subtitle || "",
                                  }}
                                />
                                <AudienceTags audience={tpl.audience} />

                                <p className="template-card-access">
                                  {tpl.access_type === "paid"
                                    ? t("full")
                                    : tpl.access_type === "free"
                                      ? t("free")
                                      : t("Membership")}
                                </p>
                                <div className="mt-auto">
                                  <button
                                    className="site-btn mt-2"
                                    style={{ minWidth: "fit-content" }}
                                    onClick={() =>
                                      navigate(`/templates/${tpl._id}`)
                                    }
                                  >
                                    {t("view")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </OwlCarousel>
                    </section>
                  );
                })}
          </div>
        </section>
      </TemplateLayout>

      <StickyActionBar
        user={user}
        userImage={userImage}
        onShare={() => setShowShareModal(true)}
        shareActive={showShareModal}
      />
      <ShareModal
        shareUrl={window.location.href}
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
      />

      {showScrollTop && (
        <button
          type="button"
          aria-label="Scroll to top"
          title="Scroll to top"
          onClick={scrollToTop}
          className="btn purple-text rounded-circle shadow scroll-to-top-btn"
        >
          <i className="bi bi-arrow-up-circle-fill fs-3"></i>
        </button>
      )}
    </>
  );
};

export default Template;
